import type {
  UncappedCrestSource,
  UntrackedUncappedCrestSource,
} from 'app/frontend/types/custom/Season'

type CrestCapSource = {
  first_period: number
  weekly_increase: number
  uncapped_crests?: Record<string, UncappedCrestSource>
  untracked_uncapped_crest?: UntrackedUncappedCrestSource
}

const sumSources = (earnedSources: Record<string, number>): number =>
  Object.values(earnedSources).reduce((sum, amount) => sum + amount, 0)

export const uncappedCrestTotal = (crest: CrestCapSource): number =>
  Object.values(crest.uncapped_crests ?? {}).reduce(
    (sum, source) => sum + source.amount,
    0,
  ) + (crest.untracked_uncapped_crest?.amount ?? 0)

export const weeklyCrestCap = (
  crest: CrestCapSource,
  currentPeriod: number,
): number =>
  Math.max(0, currentPeriod - crest.first_period + 1) * crest.weekly_increase

// The untracked source can't be read from the API, so it is only known to be
// complete when the character has more crests than the weekly cap and the
// tracked sources combined could have given them.
export const untrackedCrestComplete = (
  crest: CrestCapSource,
  currentPeriod: number,
  earned: number,
  earnedSources: Record<string, number>,
): boolean =>
  !!crest.untracked_uncapped_crest &&
  earned - sumSources(earnedSources) > weeklyCrestCap(crest, currentPeriod)

export const weeklyCrestEarned = (
  earned: number,
  weeklyCap: number,
  earnedSources: Record<string, number>,
  untrackedEarned = 0,
): number =>
  Math.min(
    weeklyCap,
    Math.max(0, earned - sumSources(earnedSources) - untrackedEarned),
  )

export const crestEarnableCap = (
  crest: CrestCapSource,
  currentPeriod: number,
): number => weeklyCrestCap(crest, currentPeriod) + uncappedCrestTotal(crest)
